import { Icon } from "@mdi/react";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { mdiTrashCan } from "@mdi/js";
import { Button } from "antd";
import axios from "axios";

import { setPostKeywords } from "api/userDocumentation";

import PostForm from "components/user-documentation/PostForm";
import PostView from "components/user-documentation/PostView";
import {
  DELETE_FILE_URL,
  DELETE_POST_URL,
  DOWNLOAD_URL,
  POST_URL,
  UPDATE_URL
} from "components/user-documentation/constants";

import { formatKeywords, parseKeywords } from "../../keywords";
import styles from "../../styles/notificationViews.module.scss";
import { NotificationItem } from "../../types";

interface NotificationEditViewProps {
  notification?: NotificationItem;
  postId?: string;
  onCancel: () => void;
  onSave: (notification: NotificationItem) => void;
  onDelete?: (notificationId: number | string) => void;
  isNew?: boolean;
}

const NotificationEditView: React.FC<NotificationEditViewProps> = ({
  notification,
  postId,
  onCancel,
  onSave,
  onDelete,
  isNew = false
}) => {
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [content, setContent] = useState("");
  const [files, setFiles] = useState<string[]>([]);
  // Held as raw text so the admin can type freely; converted to a list on save.
  const [keywordText, setKeywordText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Initialize form data from notification prop
  useEffect(() => {
    if (notification) {
      setTitle(notification.title || "");
      setTags(notification.tag || []);
      setKeywordText(formatKeywords(notification.keywords));
      setContent(notification.content || "");
      setFiles(notification.attachments || []);
    } else if (isNew) {
      // Reset form for new notifications
      setTitle("");
      setTags([]);
      setContent("");
      setFiles([]);
      setKeywordText("");
    }
  }, [notification, isNew]);

  function handleTitleChange(newTitle: string) {
    setTitle(newTitle);
  }

  function handleContentChange(newContent: string) {
    setContent(newContent);
  }

  function addFile(newFile: string) {
    setFiles((prevState) => [...prevState, newFile]);
  }

  function replace(before: string, after: string) {
    setContent((content) => {
      return content.replace(before, after);
    });
  }

  function cleanFiles() {
    const unreferencedFiles: string[] = [];
    for (let i = 0; i < files.length; i++) {
      if (!content.includes(files[i])) {
        unreferencedFiles.push(files[i]);
        axios.delete(DELETE_FILE_URL + "/" + files[i], {});
      }
    }
    const temp = [...files];
    return temp.filter((item) => !unreferencedFiles.includes(item));
  }

  // Fix image URLs in old posts that may have broken or outdated URLs
  const fixImageUrls = (content: string): string => {
    if (!content) return content;

    // Pattern to match markdown images: ![alt text](url)
    // This handles various URL patterns that might exist in old posts
    return content.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, altText, url) => {
      // If the URL already uses the current DOWNLOAD_URL, leave it as is
      if (url.includes(DOWNLOAD_URL)) {
        return match;
      }

      // Extract file ID from various possible old URL patterns
      let fileId = null;

      // Pattern 1: URLs ending with /download/{fileId} (most common in old posts)
      const downloadMatch = url.match(/\/download\/([a-zA-Z0-9]+)$/);
      if (downloadMatch) {
        fileId = downloadMatch[1];
      }

      // Pattern 2: Direct file IDs (in case some old posts stored just the ID)
      const directIdMatch = url.match(/^[a-zA-Z0-9]{24}$/); // MongoDB ObjectId pattern
      if (directIdMatch && !fileId) {
        fileId = url;
      }

      // Pattern 3: Any URL with a file ID in the path
      const pathIdMatch = url.match(/([a-zA-Z0-9]{24})/);
      if (pathIdMatch && !fileId) {
        fileId = pathIdMatch[1];
      }

      // If we found a file ID, construct the proper URL
      if (fileId) {
        return `![${altText}](${DOWNLOAD_URL}/${fileId})`;
      }

      // If we can't extract a file ID, return the original (might be an external image)
      return match;
    });
  };

  // Keywords are stored separately from the post's own fields, so they need
  // their own call. A keyword failure must not report the whole save as failed,
  // because the post itself did save.
  async function saveKeywords(savedPostId: Maybe<number>) {
    if (!savedPostId) return;
    try {
      await setPostKeywords(savedPostId, parseKeywords(keywordText));
    } catch (error) {
      toast.error("The post was saved, but its keywords were not.");
    }
  }

  function submitPost() {
    const params = {
      title: title,
      tag: tags,
      content: content,
      attachments: cleanFiles()
    };

    if (params.title === "" || params.tag.length === 0 || params.content === "") {
      toast.warning("Please fill all fields");
      return;
    }

    setIsLoading(true);

    if (isNew) {
      // Create new post
      axios
        .post(POST_URL, params)
        .then(async (response) => {
          toast.success("Post successfully created.");

          await saveKeywords(response.data?.id);

          // Create notification data with response data
          const notificationData: NotificationItem = {
            id: response.data?.id || Date.now(),
            title: title.trim(),
            content: content.trim(),
            tag: tags,
            date: new Date().toISOString().split("T")[0],
            attachments: cleanFiles(),
            // Carried so the copy the modal keeps matches what was saved.
            // Without it, reopening this post showed an empty keywords box and
            // the next save wrote that back, erasing them.
            keywords: parseKeywords(keywordText)
          };

          onSave(notificationData);

          // Reset form after successful creation
          setTitle("");
          setContent("");
          setTags([]);
          setFiles([]);
          setKeywordText("");
        })
        .catch(() => {
          toast.error("An error occurred when creating the post.");
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // Update existing post
      const updateParams = {
        ...params,
        id: notification?.id || (postId ? parseInt(postId) : undefined),
        date: notification?.date || new Date().toISOString().split("T")[0]
      };

      axios
        .put(UPDATE_URL, updateParams)
        .then(async () => {
          toast.success("Post successfully updated.");

          await saveKeywords(updateParams.id);

          // Create notification data with updated values
          const notificationData: NotificationItem = {
            id: notification?.id || (postId ? parseInt(postId) : Date.now()),
            title: title.trim(),
            content: content.trim(),
            tag: tags,
            date: notification?.date || new Date().toISOString().split("T")[0],
            attachments: cleanFiles(),
            // See the create branch: the modal's copy has to carry these.
            keywords: parseKeywords(keywordText)
          };

          onSave(notificationData);
        })
        .catch(() => {
          toast.error("An error occurred when updating the post.");
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }

  function handleCancel() {
    if (!isNew) {
      // Reset to original values if editing
      if (notification) {
        setTitle(notification.title || "");
        setTags(notification.tag || []);
        setKeywordText(formatKeywords(notification.keywords));
        setContent(notification.content || "");
        setFiles(notification.attachments || []);
      }
    } else {
      // Clear form if creating new
      setTitle("");
      setTags([]);
      setContent("");
      setFiles([]);
    }
    onCancel();
  }

  function deleteConfirmation() {
    // eslint-disable-next-line no-alert
    if (window.confirm("Are you sure you want to delete this post?")) {
      deletePost();
    }
  }

  function deletePost() {
    if (!notification?.id && !postId) {
      toast.error("Cannot delete post: missing ID");
      return;
    }

    const idToDelete = notification?.id || postId;
    setIsLoading(true);

    axios
      .delete(`${DELETE_POST_URL}/${idToDelete}`)
      .then(() => {
        toast.success("Post successfully deleted");

        // Notify parent component of deletion - this will handle selecting the next post
        if (onDelete && idToDelete) {
          onDelete(idToDelete);
        }

        // Return to post view to show the newly selected post
        onCancel();
      })
      .catch(() => {
        toast.error("An error occurred when deleting the post.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  const isFormValid = title.trim() !== "" && tags.length > 0 && content.trim() !== "";

  return (
    <div className={styles.notificationEditView}>
      <div className={styles.headerContainer}>
        <div className={styles.titleContainer}>
          <h2>{isNew ? "Create New Post" : "Edit Post"}</h2>
          {!isNew && (
            <Button
              type="text"
              onClick={deleteConfirmation}
              disabled={isLoading}
              className={styles.deleteButton}
              title="Delete post">
              <Icon path={mdiTrashCan} size="2.5rem" />
            </Button>
          )}
        </div>

        <div className={styles.actionsContainer}>
          <Button
            type="primary"
            onClick={submitPost}
            loading={isLoading}
            disabled={!isFormValid}>
            {isLoading ? "Saving..." : "Save"}
          </Button>
          <Button type="default" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
        </div>
      </div>
      <div className={styles.editContent}>
        <PostForm
          title={title}
          tags={tags}
          content={content}
          keywordText={keywordText}
          onKeywordTextChange={setKeywordText}
          replaceContent={replace}
          addFile={addFile}
          onTitleChange={handleTitleChange}
          onTagSelection={(v) => setTags(v)}
          onContentChange={handleContentChange}
          onSubmit={submitPost}
        />

        <PostView title={title} tags={tags} content={fixImageUrls(content)} />
      </div>
    </div>
  );
};

export default NotificationEditView;
